import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, BarChart3, FileSpreadsheet, FileText, Loader2, Table } from "lucide-react";
import {
  REPORT_IDS,
  REPORT_LABELS,
  adminCaseReport,
  type ReportId,
  type ReportResult,
} from "@/lib/api/admin-case-reports.functions";
import {
  exportReportCsv,
  exportReportExcel,
  exportReportPdf,
} from "@/lib/case-report-export";

export const Route = createFileRoute("/_authenticated/portal/admin/cases/reports")({
  head: () => ({
    meta: [
      { title: "Case reports · NDLCS Admin" },
      {
        name: "description",
        content: "Export matter, workload, document and revenue reports to PDF, Excel or CSV.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CaseReports,
});

const inputCls = "rounded-md border border-input bg-background px-3 py-2 text-sm";

function CaseReports() {
  const run = useServerFn(adminCaseReport);
  const [report, setReport] = useState<ReportId>("open-closed");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [result, setResult] = useState<ReportResult | null>(null);

  const gen = useMutation({
    mutationFn: () => run({ data: { report, from, to } }),
    onSuccess: (r: any) => setResult(r as ReportResult),
  });

  return (
    <div className="space-y-5 py-4">
      <Link
        to="/portal/admin/cases"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All matters
      </Link>

      <header>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
          <BarChart3 className="h-3.5 w-3.5" /> Reports
        </p>
        <h1 className="font-display text-2xl text-navy">Case reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Generate a report, then export it to PDF, Excel or CSV.
        </p>
      </header>

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          gen.mutate();
        }}
      >
        <div>
          <label htmlFor="rep" className="text-xs font-medium text-muted-foreground">
            Report
          </label>
          <select
            id="rep"
            className={`mt-1 block ${inputCls}`}
            value={report}
            onChange={(e) => setReport(e.target.value as ReportId)}
          >
            {REPORT_IDS.map((r) => (
              <option key={r} value={r}>
                {REPORT_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rep-from" className="text-xs font-medium text-muted-foreground">
            From
          </label>
          <input
            id="rep-from"
            type="date"
            className={`mt-1 block ${inputCls}`}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="rep-to" className="text-xs font-medium text-muted-foreground">
            To
          </label>
          <input
            id="rep-to"
            type="date"
            className={`mt-1 block ${inputCls}`}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={gen.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Generate
        </button>
      </form>

      {gen.error ? (
        <p className="text-sm text-red-700">{String((gen.error as any).message)}</p>
      ) : null}

      {result ? (
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg text-navy">{result.title}</h2>
              <p className="text-xs text-muted-foreground">
                {result.rows.length} rows · generated{" "}
                {new Date(result.generatedAt).toLocaleString()}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => exportReportPdf(result)}
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <FileText className="h-3.5 w-3.5" /> PDF
              </button>
              <button
                type="button"
                onClick={() => exportReportExcel(result)}
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
              </button>
              <button
                type="button"
                onClick={() => exportReportCsv(result)}
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <Table className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
          </div>

          {result.summary?.length ? (
            <div className="flex flex-wrap gap-4 rounded-md bg-muted/40 p-3 text-sm">
              {result.summary.map((s) => (
                <p key={s.label}>
                  <span className="text-muted-foreground">{s.label}: </span>
                  <strong className="text-navy">{s.value}</strong>
                </p>
              ))}
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {result.columns.map((c) => (
                    <th key={c.key} className="px-3 py-2">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={result.columns.length}
                      className="px-3 py-4 text-muted-foreground"
                    >
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  result.rows.slice(0, 200).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      {result.columns.map((c) => (
                        <td key={c.key} className="px-3 py-2">
                          {String(r[c.key] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {result.rows.length > 200 ? (
            <p className="text-xs text-muted-foreground">
              Showing the first 200 rows — exports contain all {result.rows.length}.
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
